/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Grid } from '#UNSAFE_Grid';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { IsoMonth } from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps } from 'preact/compat';
import { range } from 'ramda';
import { MonthCell } from './MonthCell';
import { BundleType } from '#resources/nls/bundle';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';

const MONTHS = range(1, 13) as IsoMonth[];

type PickedMonthCellProps = Pick<ComponentProps<typeof MonthCell>, 'onAction' | 'onNavigation'>;
type Props = PickedMonthCellProps &
  TestIdProps & {
    /**
     * Specifies the current month determined from the today's date.
     */
    currentMonth?: IsoMonth;

    /**
     * Specifies which month cell should be focused first when tabbing
     * into the MonthsGridView.
     */
    focusableMonth: IsoMonth;

    /**
     * Specifies if the focusableMonth month cell is currently focused.
     * @default false
     */
    isFocused?: boolean;

    /**
     * Specifies which month cell is currently selected.
     */
    selectedMonth?: IsoMonth;
  };

/**
 * This component renders the Months Grid View for the DatePicker component.
 */
export const MonthsGridView = ({
  currentMonth,
  focusableMonth,
  isFocused = false,
  selectedMonth,
  testId,
  onAction,
  onNavigation
}: Props) => {
  // Lay out the Months in a 3-row grid.
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const monthsAriaLabel = translations.datePicker_months();

  return (
    <Grid role="grid" gap={['13.5x', '0x']} testId={testId} aria-label={monthsAriaLabel}>
      {[0, 1, 2].map((rowIndex) => (
        <Grid key={rowIndex} role="row" gap={['0x', '3x']} gridTemplateColumns="repeat(4, 1fr)">
          {[0, 1, 2, 3].map((cellIndex) => {
            const month = MONTHS[rowIndex * 4 + cellIndex];
            return (
              <div role="gridcell">
                <MonthCell
                  key={`${month}`}
                  month={month}
                  isCurrent={month === currentMonth}
                  isFocusable={month === focusableMonth}
                  isFocused={month === focusableMonth && isFocused}
                  isSelected={month === selectedMonth}
                  onAction={onAction}
                  onNavigation={onNavigation}
                />
              </div>
            );
          })}
        </Grid>
      ))}
    </Grid>
  );
};
